// Shop window: Orbs / Shop / Support tabs. Orbs roll a random cosmetic (a
// duplicate pays back 1/3 of its price) and open in their own window; the
// shop sells hats, robes, wands and faces for coins, with a live 3D preview of the
// picked item on your wizard. The server does the rolling and the paying.
import { lazy, Suspense, useMemo, useState } from "react";
import {
  DEFAULT_EQUIPPED,
  ORB_DUPLICATE_REFUND_RATE,
  ORB_NAMES,
  ORB_ODDS,
  ORB_PRICES,
  ORB_TIERS,
  RARITY_LABEL,
  RARITY_ORDER,
  SHOP_ITEMS,
  WOW_ODDS_LABEL,
  ownedItemIds,
  wizardLook,
  type ItemDef,
  type Me,
  type OrbResult,
  type OrbTier,
  type Rarity,
  type WizardLook,
} from "@wp/shared";
import { formatCoins } from "../../lib/format";
import { api } from "../../net/api";
import { useSettings } from "../../settings/SettingsContext";
import { Icon } from "./Icon";
import { ItemPreviewView } from "./ItemPreviewView";
import { ItemThumb } from "./ItemThumb";
import { Modal } from "./Modal";
import { SupportPanel } from "./SupportPanel";
import { Tabs } from "./Tabs";

// The orb window brings three.js with it (already loaded behind the loading screen).
const OrbOpeningWindow = lazy(() => import("./OrbOpening").then((m) => ({ default: m.OrbOpeningWindow })));

type ShopTab = "orbs" | "shop" | "support";
const TABS = [
  { id: "orbs", label: "Orbs" },
  { id: "shop", label: "Shop" },
  { id: "support", label: "Support" },
] as const;

/** Rarities from common up (the order the odds tables read in). */
export const SHOP_RARITY_ORDER: readonly Rarity[] = [...RARITY_ORDER].reverse();

const ALL_SHELVES = [
  { id: "hat", label: "Hats" },
  { id: "robe", label: "Robes" },
  { id: "wand", label: "Wands" },
  { id: "reaction", label: "Faces" },
] as const;
type Shelf = (typeof ALL_SHELVES)[number]["id"];
/** Shelves with something on sale (an empty one is not shown). */
const SHELVES = ALL_SHELVES.filter((s) => SHOP_ITEMS.some((i) => i.category === s.id));

interface Props {
  me: Me;
  onMeChange: (me: Me) => void;
  onClose: () => void;
  /** Buying is paused (for example mid-hand, when your coins are on the table). */
  blockedReason?: string | null;
}

export function ShopWindow({ me, onMeChange, onClose, blockedReason = null }: Props) {
  const [tab, setTab] = useState<ShopTab>("orbs");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pull, setPull] = useState<OrbResult | null>(null);
  const owned = useMemo(() => new Set(ownedItemIds(me.unlocks)), [me.unlocks]);
  const { settings } = useSettings();
  const calm = settings.accessibility.photosensitive;
  const animate = !settings.game.reduceAnimations && !calm;
  const look = useMemo(() => wizardLook({ ...DEFAULT_EQUIPPED, ...me.equipped }), [me.equipped]);

  /** Free pulls (weekly login rewards) are spent before coins. */
  const freePulls = (tier: OrbTier) => me.freeOrbs[tier] ?? 0;

  async function openOrb(tier: OrbTier) {
    setBusy(true);
    setError(null);
    const res = await api.openOrb(tier, freePulls(tier) > 0);
    setBusy(false);
    if (!res.ok) return setError(res.error.message);
    setPull(res.value);
    onMeChange(res.value.me);
  }

  async function buy(item: ItemDef) {
    setBusy(true);
    setError(null);
    const res = await api.buyItem(item.id);
    setBusy(false);
    if (!res.ok) return setError(res.error.message);
    onMeChange(res.value);
  }

  return (
    <Modal title="Shop" onClose={onClose} testId="shop-window" className="wide shop">
      <div className="shop-top">
        <Tabs tabs={TABS} active={tab} onChange={setTab} label="Shop sections" />
        <span className="shop-coins" data-testid="shop-coins">
          <Icon name="coin" size={16} /> {formatCoins(me.coins)}
        </span>
      </div>
      {blockedReason && <p className="cant-cast small">{blockedReason}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div role="tabpanel" className="tab-panel">
        {tab === "orbs" && (
          <div className="orbs">
            <p className="muted small">
              Each orb gives a random item from the shop. Already have it? You get 1/3 ({Math.round(ORB_DUPLICATE_REFUND_RATE * 100)}%) of its shop
              price back in coins. Free pulls from your weekly login rewards are used first.
            </p>
            <div className="orb-grid">
              {ORB_TIERS.map((tier) => (
                <section key={tier} className={`orb-card ${tier}`} data-testid={`orb-${tier}`}>
                  <div className="orb-art" aria-hidden="true" />
                  <h3>{ORB_NAMES[tier]}</h3>
                  <p className="orb-price">
                    <Icon name="coin" size={14} /> {formatCoins(ORB_PRICES[tier])}
                  </p>
                  <table className="odds">
                    <tbody>
                      {SHOP_RARITY_ORDER.map((r) => (
                        <tr key={r}>
                          <th scope="row" className={`rarity ${r}`}>
                            {RARITY_LABEL[r]}
                          </th>
                          <td>{r === "wow" ? WOW_ODDS_LABEL : `${ORB_ODDS[tier][r]}%`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button
                    type="button"
                    className={freePulls(tier) > 0 ? "btn gold" : "btn primary"}
                    disabled={busy || blockedReason !== null || (freePulls(tier) < 1 && me.coins < ORB_PRICES[tier])}
                    onClick={() => void openOrb(tier)}
                    data-testid={`orb-open-${tier}`}
                  >
                    {freePulls(tier) > 0 ? `Free pull ×${freePulls(tier)}` : me.coins < ORB_PRICES[tier] ? "Not enough coins" : "Open"}
                  </button>
                </section>
              ))}
            </div>
          </div>
        )}
        {tab === "shop" && (
          <ShopShelves owned={owned} coins={me.coins} busy={busy || blockedReason !== null} look={look} animate={animate} onBuy={(i) => void buy(i)} />
        )}
        {tab === "support" && (
          <SupportPanel testId="shop-support" />
        )}
      </div>
      {pull && (
        <Suspense fallback={null}>
          <OrbOpeningWindow
            key={`${pull.itemId}:${pull.me.coins}:${pull.me.freeOrbs[pull.tier]}`}
            pull={pull}
            look={look}
            calm={calm}
            quick={!animate}
            canAgain={!busy && (freePulls(pull.tier) > 0 || me.coins >= ORB_PRICES[pull.tier]) && blockedReason === null}
            onAgain={() => void openOrb(pull.tier)}
            onClose={() => setPull(null)}
          />
        </Suspense>
      )}
    </Modal>
  );
}

function ShopShelves({
  owned,
  coins,
  busy,
  look,
  animate,
  onBuy,
}: {
  owned: ReadonlySet<string>;
  coins: number;
  busy: boolean;
  look: WizardLook;
  animate: boolean;
  onBuy: (item: ItemDef) => void;
}) {
  const [shelf, setShelf] = useState<Shelf>("hat");
  const [picked, setPicked] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const items = useMemo(
    () =>
      SHOP_ITEMS.filter((i) => i.category === shelf).sort(
        (a, b) => SHOP_RARITY_ORDER.indexOf(a.rarity) - SHOP_RARITY_ORDER.indexOf(b.rarity) || a.price! - b.price! || a.name.localeCompare(b.name),
      ),
    [shelf],
  );
  // The preview shows the picked item, or the first one on the shelf.
  const shown = items.find((i) => i.id === picked) ?? items[0]!;

  /** Buy (with a confirm step) or "Owned"; `where` keeps test ids apart for the card and the preview. */
  function buyButton(item: ItemDef, where: "shop" | "preview") {
    const price = item.price!;
    if (owned.has(item.id)) return <span className="owned-tag">Owned</span>;
    if (confirm === item.id) {
      return (
        <span className="buy-confirm">
          <button
            type="button"
            className="btn small primary"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              setConfirm(null);
              onBuy(item);
            }}
            data-testid={`${where}-confirm-${item.id}`}
          >
            Buy for {formatCoins(price)}
          </button>
          <button
            type="button"
            className="btn small"
            onClick={(e) => {
              e.stopPropagation();
              setConfirm(null);
            }}
          >
            No
          </button>
        </span>
      );
    }
    return (
      <button
        type="button"
        className="btn small buy"
        disabled={busy || coins < price}
        onClick={(e) => {
          e.stopPropagation();
          setPicked(item.id);
          setConfirm(item.id);
        }}
        data-testid={`${where}-buy-${item.id}`}
      >
        <Icon name="coin" size={13} /> {formatCoins(price)}
      </button>
    );
  }

  return (
    <div className="shop-shelves">
      <div className="shelf-picker" role="group" aria-label="Item type">
        {SHELVES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`chip ${shelf === s.id ? "active" : ""}`}
            aria-pressed={shelf === s.id}
            onClick={() => {
              setShelf(s.id);
              setPicked(null);
              setConfirm(null);
            }}
            data-testid={`shelf-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="shop-body">
        <aside className={`shop-preview rarity-${shown.rarity}`} aria-label="Preview">
          <ItemPreviewView item={shown} look={look} animate={animate} />
          <strong className="item-name">{shown.name}</strong>
          <span className={`rarity ${shown.rarity}`}>{RARITY_LABEL[shown.rarity]}</span>
          <span className="item-desc small muted">{shown.description}</span>
          {buyButton(shown, "preview")}
          <span className="small muted preview-hint">Drag the picture to turn it.</span>
        </aside>
        <ul className="shop-grid">
          {items.map((item) => (
            <li
              key={item.id}
              className={`shop-item rarity-${item.rarity} ${item.id === shown.id ? "picked" : ""}`}
              onClick={() => setPicked(item.id)}
              data-testid={`shop-item-${item.id}`}
            >
              <button type="button" className="shop-pick" aria-pressed={item.id === shown.id} aria-label={`Preview ${item.name}`} onClick={() => setPicked(item.id)}>
                <ItemThumb item={item} size={84} />
              </button>
              <span className="item-name">{item.name}</span>
              <span className={`rarity ${item.rarity}`}>{RARITY_LABEL[item.rarity]}</span>
              {buyButton(item, "shop")}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
